import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getManualLedgers, deleteManualLedger } from '../firebase/database';
import { generateManualLedgerPDF, generateManualLedgerExcel, formatDate } from '../services/manualLedgerPdfService';
import { openPDFInNewTab } from '../services/invoicePdfService';
import { ManualLedger } from '../types';
import DateRangeModal from './DateRangeModal';
import toast, { Toaster } from 'react-hot-toast';

const ManualLedgersList: React.FC = () => {
    const navigate = useNavigate();
    const [ledgers, setLedgers] = useState<ManualLedger[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [sortBy, setSortBy] = useState<'partyName' | 'createdAt'>('partyName');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [loading, setLoading] = useState<boolean>(true);
    const [generatingPDF, setGeneratingPDF] = useState<string | null>(null);
    const [deletingLedger, setDeletingLedger] = useState<string | null>(null);
    const [showDateModal, setShowDateModal] = useState(false);
    const [selectedLedger, setSelectedLedger] = useState<ManualLedger | null>(null);
    const [dateRange, setDateRange] = useState<{ min: string; max: string }>({ min: '', max: '' });

    useEffect(() => {
        loadLedgers();
    }, []);

    const loadLedgers = async () => {
        try {
            setLoading(true);
            const data = await getManualLedgers();
            setLedgers(data);
        } catch (error) {
            console.error('Error loading manual ledgers:', error);
            toast.error('Error loading manual ledgers. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Calculate date range from ledger entries
    const calculateDateRange = (ledger: ManualLedger) => {
        if (!ledger.entries || ledger.entries.length === 0) {
            const today = new Date();
            const todayStr = today.toISOString().slice(0, 10); // YYYY-MM-DD format
            return { min: todayStr, max: todayStr };
        }

        const dates = ledger.entries.map(entry => new Date(entry.date));
        const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
        const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
        
        return {
            min: minDate.toISOString().slice(0, 10), // YYYY-MM-DD format
            max: maxDate.toISOString().slice(0, 10)  // YYYY-MM-DD format
        };
    };

    const handleViewPDF = (ledger: ManualLedger) => {
        if (!ledger.id) {
            toast.error('Error: Ledger ID is missing. Cannot generate PDF.');
            return;
        }

        const range = calculateDateRange(ledger);
        setDateRange(range);
        setSelectedLedger(ledger);
        setShowDateModal(true);
    };

    const handleViewExcel = (ledger: ManualLedger) => {
        if (!ledger.id) {
            toast.error('Error: Ledger ID is missing. Cannot generate Excel.');
            return;
        }
        try {
            generateManualLedgerExcel(ledger);
        } catch (error) {
            console.error('Error generating Excel:', error);
            toast.error('Error generating Excel. Please try again.');
        }
    };

    const handleDateRangeConfirm = async (fromDate: string, toDate: string) => {
        if (!selectedLedger) return;

        let filteredLedger = { ...selectedLedger };

        // Filter entries by date range if dates are provided
        if (fromDate || toDate) {
            // For date-only inputs, we need to include the entire day
            const from = fromDate ? new Date(fromDate + 'T00:00:00') : new Date(0);
            const to = toDate ? new Date(toDate + 'T23:59:59') : new Date(9999, 11, 31);
            
            // Sort all entries by date to calculate opening balance correctly
            const sortedEntries = [...selectedLedger.entries].sort((a, b) => {
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            });
            
            // Calculate opening balance from entries before the "from" date
            let openingBalance = selectedLedger.openingBalance || 0;
            if (fromDate) {
                // Calculate balance from all entries before the "from" date
                for (const entry of sortedEntries) {
                    const entryDate = new Date(entry.date);
                    if (entryDate < from) {
                        // Credit adds, debit subtracts
                        openingBalance += (entry.credit || 0) - (entry.debit || 0);
                    } else {
                        break; // Entries are sorted, so we can stop here
                    }
                }
            }
            
            // Filter entries within the date range
            filteredLedger.entries = sortedEntries.filter(entry => {
                const entryDate = new Date(entry.date);
                return entryDate >= from && entryDate <= to;
            });
            
            // Set the calculated opening balance for the filtered ledger
            filteredLedger.openingBalance = openingBalance;

            if (filteredLedger.entries.length === 0) {
                toast.error('No entries found for the specified date range.');
                return;
            }
        }

        try {
            setGeneratingPDF(selectedLedger.id!);
            const pdf = await generateManualLedgerPDF(filteredLedger);
            openPDFInNewTab(pdf, `Manual Ledger ${selectedLedger.partyName}`);
        } catch (error) {
            console.error('Error generating PDF:', error, { ledger: selectedLedger });
            toast.error(`Error generating PDF: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
        } finally {
            setGeneratingPDF(null);
        }
    };

    const handleDeleteLedger = async (ledger: ManualLedger) => {
        if (!ledger.id) {
            toast.error('Error: Ledger ID is missing. Cannot delete ledger.');
            return;
        }

        const confirmDelete = window.confirm(
            `Are you sure you want to delete the ledger for "${ledger.partyName}"? This action cannot be undone.`
        );

        if (!confirmDelete) {
            return;
        }

        try {
            setDeletingLedger(ledger.id);
            await deleteManualLedger(ledger.id);
            toast.success('Ledger deleted successfully!');
            loadLedgers(); // Reload the list
        } catch (error) {
            console.error('Error deleting ledger:', error);
            toast.error(`Error deleting ledger: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
        } finally {
            setDeletingLedger(null);
        }
    };

    const handleSort = (field: 'partyName' | 'createdAt') => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const getSortIcon = (field: 'partyName' | 'createdAt') => {
        if (sortBy !== field) {
            return (
                <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            );
        }

        return sortOrder === 'asc' ? (
            <svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
        ) : (
            <svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
        );
    };

    // Calculate stats
    const totalOutstanding = ledgers.reduce((sum, ledger) => {
        const lastEntry = ledger.entries[ledger.entries.length - 1];
        return sum + (lastEntry ? lastEntry.balance : 0);
    }, 0);
    const averageOutstanding = ledgers.length > 0 ? totalOutstanding / ledgers.length : 0;

    // Filter and sort ledgers
    const filteredLedgers = ledgers
        .filter(ledger =>
            ledger.partyName.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'partyName':
                    comparison = a.partyName.localeCompare(b.partyName);
                    break;
                case 'createdAt':
                    comparison = (a.createdAt ? new Date(a.createdAt).getTime() : 0) - (b.createdAt ? new Date(b.createdAt).getTime() : 0);
                    break;
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading manual ledgers...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 rounded-lg">
            <Toaster />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Manual Ledgers</h1>
                    <div className="flex space-x-4">
                        <button
                            onClick={() => navigate('/create-ledger')}
                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                        >
                            Add Manual Ledger
                        </button>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                        >
                            Back to Dashboard
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                                        <span className="text-white text-lg">📊</span>
                                    </div>
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">Total Ledgers</dt>
                                        <dd className="text-lg font-medium text-gray-900">{ledgers.length}</dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                                        <span className="text-white text-lg">💰</span>
                                    </div>
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">Total Outstanding</dt>
                                        <dd className="text-lg font-medium text-gray-900">PKR {Math.round(totalOutstanding).toLocaleString()}</dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                                        <span className="text-white text-lg">📈</span>
                                    </div>
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">Average Outstanding</dt>
                                        <dd className="text-lg font-medium text-gray-900">PKR {Math.round(averageOutstanding).toLocaleString()}</dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-white shadow rounded-lg mb-6">
                    <div className="px-4 py-5 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                            <div className="flex-1 max-w-lg">
                                <label htmlFor="search" className="sr-only">Search ledgers</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <input
                                        id="search"
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Search ledgers by party name..."
                                    />
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                <span className="text-sm text-gray-700">Sort by:</span>
                                                                    <select
                                        value={`${sortBy}-${sortOrder}`}
                                        onChange={(e) => {
                                            const [field, order] = e.target.value.split('-') as ['partyName' | 'createdAt', 'asc' | 'desc'];
                                            setSortBy(field);
                                            setSortOrder(order);
                                        }}
                                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                    >
                                        <option value="partyName-asc">Party Name (A-Z)</option>
                                        <option value="partyName-desc">Party Name (Z-A)</option>
                                        <option value="createdAt-asc">Date Added (Oldest First)</option>
                                        <option value="createdAt-desc">Date Added (Newest First)</option>
                                    </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Ledgers Table */}
                <div className="bg-white shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900">
                                Manual Ledgers ({filteredLedgers.length} of {ledgers.length})
                            </h3>
                        </div>

                        {filteredLedgers.length === 0 ? (
                            <div className="text-center py-8">
                                <div className="text-4xl mb-4">🔍</div>
                                <h4 className="text-lg font-medium text-gray-900 mb-2">No ledgers found</h4>
                                <p className="text-gray-500">Try adjusting your search criteria.</p>
                            </div>
                        ) : (
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                <button
                                                    onClick={() => handleSort('partyName')}
                                                    className="group inline-flex items-center space-x-1 hover:text-gray-700"
                                                >
                                                    <span>Party Name</span>
                                                    {getSortIcon('partyName')}
                                                </button>
                                            </th>

                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                <button
                                                    onClick={() => handleSort('createdAt')}
                                                    className="group inline-flex items-center space-x-1 hover:text-gray-700"
                                                >
                                                    <span>Date Added</span>
                                                    {getSortIcon('createdAt')}
                                                </button>
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredLedgers.map((ledger) => (
                                            <tr key={ledger.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{ledger.partyName}</div>
                                                        <div className="text-sm text-gray-500">ID: {ledger.id}</div>
                                                    </div>
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {ledger.createdAt ? formatDate(ledger.createdAt) : 'N/A'}
                                                </td>
                                                                                                 <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                     <div className="flex space-x-3">
                                                         <button
                                                             className="text-indigo-600 hover:text-indigo-900"
                                                             onClick={() => navigate(`/edit-manual-ledger/${ledger.id}`)}
                                                         >
                                                             Edit
                                                         </button>
                                                         <button
                                                             className="text-green-600 hover:text-green-900"
                                                             onClick={() => handleViewPDF(ledger)}
                                                             disabled={generatingPDF === ledger.id}
                                                         >
                                                             {generatingPDF === ledger.id ? 'Generating...' : 'View PDF'}
                                                         </button>
                                                         <button
                                                             className="text-blue-600 hover:text-blue-900"
                                                             onClick={() => handleViewExcel(ledger)}
                                                         >
                                                             View Excel
                                                         </button>
                                                         <button
                                                             className="text-red-600 hover:text-red-900"
                                                             onClick={() => handleDeleteLedger(ledger)}
                                                             disabled={deletingLedger === ledger.id}
                                                         >
                                                             {deletingLedger === ledger.id ? 'Deleting...' : 'Delete'}
                                                         </button>
                                                     </div>
                                                 </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Date Range Modal */}
            <DateRangeModal
                isOpen={showDateModal}
                onClose={() => setShowDateModal(false)}
                onConfirm={handleDateRangeConfirm}
                minDate={dateRange.min}
                maxDate={dateRange.max}
                title="Select Date Range for PDF"
            />
        </div>
    );
};

export default ManualLedgersList;